import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/util/Reveal";
import { SoftwareCard } from "@/components/cards/SoftwareCard";
import { api } from "@/lib/api";

export const SoftwareSection = ({ software }) => {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const ctaLabel = s.software_section_cta_label || "Browse Software";
  const ctaLink = s.software_section_cta_link || "/software";
  // Reuse the same 4 fields already read by /software PageHero so editing
  // in one place updates both surfaces — same sync pattern as Phase 8.2.2
  // for the Creator Setups sidebar. Same source of truth, no duplicates.
  const eyebrow = s.software_eyebrow || "Professional Software";
  const heading = s.software_heading || "Genuine Software";
  const accent = s.software_heading_accent ?? "For Creators & Businesses";
  const description = s.software_hero_description || "100% genuine licenses. Official partners. Stay secure, updated and on top.";
  const accentInline = !!s.software_accent_inline;
  const showDot = !!s.software_show_dot;
  return (
    <section data-testid="software-section" className="bg-[#0F0F0F] pt-20 pb-28 sm:pb-32">
      <div className="max-w-[1400px] min-[1440px]:max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 min-[1440px]:px-[clamp(2.5rem,4vw,4rem)] grid lg:grid-cols-[1fr_2.4fr] gap-10 items-center">
        <Reveal>
          <div className="ur-eyebrow">
            {eyebrow}
          </div>
          <h2 className="font-display mt-4 text-[36px] sm:text-4xl font-black tracking-tight leading-[1.1] text-white">
            {heading}
            {accent && (
              accentInline
                ? <span className="text-[#FF6600]">{accent}</span>
                : <>
                    <br />
                    <span className="text-[#FF6600]">{accent}</span>
                  </>
            )}
          </h2>
          {showDot && <div className="mt-4 flex"><span className="w-2 h-2 rounded-full bg-[#FF6600]" data-testid="software-section-dot" /></div>}
          <p className="mt-4 text-[#B5B5B5] text-sm leading-relaxed max-w-sm">
            {description}
          </p>
          <Link
            to={ctaLink}
            data-testid="browse-software-link"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white border border-white/15 rounded-full px-5 py-2.5 hover:border-[#FF6600] hover:text-[#FF6600] transition-colors"
          >
            {ctaLabel} <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-4 items-stretch">
          {/* Product-first naming — homepage cards show the software product
              (e.g. "Adobe Creative Cloud"), not the publisher. Cap at the
              first 8 products (curated by display_order) so the homepage
              stays a curated professional catalog rather than the full
              /software list. 4×2 grid keeps every card the exact same
              width across both rows — no row-2 stretching. */}
          {software.slice(0, 8).map((sw, i) => (
            <Reveal key={sw.id} delay={Math.min(i * 40, 150)} className="h-full">
              <SoftwareCard
                testId={`software-card-${sw.id}`}
                logo={sw.logo}
                name={sw.name}
                subtitle={sw.tagline || sw.subtitle}
                href={`/software/${sw.slug || sw.id}`}
                size="compact"
              />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
